/* ============================================================
   storage — trimming a video.

   Cut a clip down to the part worth keeping — one range, or
   several joined end to end — inside the player, without the
   download / edit / upload-again round trip. The server does the
   cutting (see "Trimming" in server/nas.py); this is everything a
   person touches:

     TrimTools       the panel in the player: a strip over the
                     video's length with the parts to keep, handles
                     to drag, in/out at the playhead (I / O), exact
                     times to type, and fast-or-precise
     useTrimJobs     every trim you have on the server, polled while
                     one is running, with a toast when it lands —
                     so a long encode can run while you browse
     TrimJobs        that list, in the transfers panel
     TrimNotice      "trimmed just now · Undo", on the video itself

   A result waits in a cache on the server until you look at it
   and decide: save it as a new file, put it in place of the
   original (owner or admin — the file keeps its id, so its links
   keep working, and the original is kept for a week as an undo),
   or throw it away.

   Fast and precise are different trades, and the panel says which
   one you are making: fast copies the picture untouched but can
   only start a cut on a keyframe, so it shows exactly where each
   cut will really begin; precise re-encodes and lands on the frame.
   The default follows from the cuts — fast when every in point is
   on or within half a second of a keyframe, precise when not —
   until you choose.
   ============================================================ */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { Icon, fmtBytes, fmtDate } from './lib';
import { ConfirmModal, Modal, MoveModal } from './pickers';

const MIN_PART = 0.1;           // seconds — the server's floor too
const DRIFT_OK = 0.5;           // a fast cut this close to the in point is close enough
const DRAFT_KEY = 'st.trim.drafts';
const ACTIVE = ['queued', 'working', 'committing'];

/* ─── time ────────────────────────────────────────────────────────────── */

const p2 = (n) => String(n).padStart(2, '0');

/** 1:05.250 — or 1:02:03.500 past an hour. Milliseconds, because a trim is
 *  about frames and a frame at 60 fps is 16 of them. */
export function fmtClock(s) {
  if (!Number.isFinite(s)) return '--:--';
  const ms = Math.round(Math.max(0, s) * 1000);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const sec = (ms % 60000) / 1000;
  const ss = sec.toFixed(3).padStart(6, '0');
  return h ? `${h}:${p2(m)}:${ss}` : `${m}:${ss}`;
}

/** 00:12 — the short form a file name wants. */
function fmtShort(s) {
  const t = Math.floor(Math.max(0, s));
  const h = Math.floor(t / 3600);
  return h ? `${h}:${p2(Math.floor((t % 3600) / 60))}:${p2(t % 60)}` : `${p2(Math.floor(t / 60))}:${p2(t % 60)}`;
}

/** Seconds from what someone typed: "65.25", "1:05.25", "1:02:03". */
export function parseClock(text) {
  const raw = String(text || '').trim().replace(',', '.');
  if (!raw) return null;
  const parts = raw.split(':');
  if (parts.length > 3 || parts.some((p) => !/^\d*\.?\d*$/.test(p) || p === '' || p === '.')) return null;
  const nums = parts.map(Number);
  if (nums.some((n) => !Number.isFinite(n))) return null;
  if (nums.slice(1).some((n) => n >= 60)) return null;
  return nums.reduce((acc, n) => acc * 60 + n, 0);
}

function fmtDur(s) {
  if (!Number.isFinite(s)) return '—';
  if (s < 60) return `${s.toFixed(s < 10 ? 2 : 1)}s`;
  return fmtShort(s).replace(/^0(\d)/, '$1');
}

/** The last keyframe at or before t — where a fast cut starting at t begins. */
export function kfBefore(kfs, t) {
  if (!kfs || !kfs.length) return null;
  let lo = 0;
  let hi = kfs.length - 1;
  if (kfs[0] > t + 1e-6) return kfs[0];
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (kfs[mid] <= t + 1e-6) lo = mid; else hi = mid - 1;
  }
  return kfs[lo];
}

function nearestKf(kfs, t) {
  if (!kfs || !kfs.length) return null;
  const b = kfBefore(kfs, t);
  const i = kfs.indexOf(b);
  const n = kfs[i + 1];
  return n !== undefined && Math.abs(n - t) < Math.abs(t - b) ? n : b;
}

/** Touching parts are one part: a split nobody opened a gap in removes nothing. */
function mergeParts(segs) {
  const out = [];
  for (const [a, b] of [...segs].sort((x, y) => x[0] - y[0])) {
    const last = out[out.length - 1];
    if (last && a - last[1] < 0.002) last[1] = Math.max(last[1], b);
    else out.push([a, b]);
  }
  return out;
}

const keptOf = (segs) => segs.reduce((n, [a, b]) => n + (b - a), 0);

/** "clip (trim 00:12–01:05).mp4" — what a saved copy is called unless renamed. */
export function suggestName(job) {
  const src = job.file_current_name || job.file_name || 'video';
  const dot = src.lastIndexOf('.');
  const stem = dot > 0 ? src.slice(0, dot) : src;
  const parts = job.cuts || job.segments || [];
  let what;
  if (parts.length <= 2) what = parts.map(([a, b]) => `${fmtShort(a)}–${fmtShort(b)}`).join(', ');
  else what = `${parts.length} parts, ${fmtShort(parts[0][0])}–${fmtShort(parts[parts.length - 1][1])}`;
  return `${stem} (trim ${what}).${job.out_ext}`;
}

function readDrafts() {
  try { return JSON.parse(window.localStorage.getItem(DRAFT_KEY) || '{}') || {}; } catch { return {}; }
}
function writeDraft(fileId, draft) {
  try {
    const all = readDrafts();
    if (draft) all[fileId] = { ...draft, at: Date.now() };
    else delete all[fileId];
    const keys = Object.keys(all).sort((a, b) => (all[a].at || 0) - (all[b].at || 0));
    while (keys.length > 30) delete all[keys.shift()];
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(all));
  } catch { /* private mode: the draft lasts as long as the panel */ }
}

const STATUS = {
  queued: 'waiting', working: 'trimming', ready: 'ready', committing: 'saving',
  committed: 'saved', failed: 'failed', cancelled: 'cancelled', evicted: 'expired', undone: 'undone',
};

function notify(title, body) {
  try {
    if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
      // eslint-disable-next-line no-new
      new Notification(title, { body, icon: '/storage-icon.svg', tag: 'st-trim' });
    }
  } catch { /* some browsers only allow this from a service worker */ }
}

/* ─── the jobs, for the whole app ─────────────────────────────────────── */

/**
 * Every trim this person has on the server. Polled quickly while one is on
 * its way and slowly otherwise; a change of state that someone would want to
 * know about (ready, failed, saved) becomes a toast, and a system
 * notification too when the tab is in the background and they allowed one.
 */
export function useTrimJobs(token, { toast, drives, onChanged, onReplaced, onShowFile, onOpenFile } = {}) {
  const [jobs, setJobs] = useState([]);
  const [dialog, setDialog] = useState(null);      // {kind: 'save' | 'replace', job}
  const [focus, setFocus] = useState(null);        // {fileId, jobId} — open the player's trim panel on this
  const seen = useRef(null);                       // id → status, from the last poll
  const timer = useRef(0);
  const tickRef = useRef(null);
  const cbs = useRef({});
  cbs.current = { toast, onChanged, onReplaced, onShowFile, onOpenFile };

  const load = useCallback(async () => {
    if (!token) return [];
    let list;
    try { list = (await api.trimJobs(token)).jobs || []; } catch { return null; }
    const before = seen.current;
    const { toast: say, onChanged: changed, onReplaced: replaced } = cbs.current;
    if (before) {
      for (const j of list) {
        const was = before.get(j.id);
        if (!was || was === j.status) continue;
        if (j.status === 'ready' && (was === 'working' || was === 'queued')) {
          say?.('ok', 'Trim ready', `${j.file_name} — preview it, then save or replace`);
          notify('Trim ready', j.file_name);
        } else if (j.status === 'ready' && was === 'committing' && j.error) {
          say?.('err', 'Could not save the trim', j.error);
        } else if (j.status === 'failed') {
          say?.('err', 'Trim failed', `${j.file_name}: ${j.error || 'unknown error'}`);
          notify('Trim failed', j.file_name);
        } else if (j.status === 'committed' && was === 'committing') {
          if (j.commit_kind === 'replace') {
            say?.('ok', 'Original replaced', `${j.file_current_name || j.file_name} — undo from Transfers for a week`);
            replaced?.(j.file_id);
          } else {
            say?.('ok', 'Trim saved', j.commit_name || j.file_name);
          }
          changed?.();
        }
      }
    }
    seen.current = new Map(list.map((j) => [j.id, j.status]));
    setJobs(list);
    return list;
  }, [token]);

  // Quick while something is moving, slow otherwise, never in a hidden tab
  // unless something is moving (the notification is the point then).
  useEffect(() => {
    if (!token) return undefined;
    let dead = false;
    const moving = () => !!seen.current && [...seen.current.values()].some((st) => ACTIVE.includes(st));
    const tick = async () => {
      window.clearTimeout(timer.current);
      if (!document.hidden || moving()) await load();
      if (dead) return;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(tick, moving() ? 1500 : 30000);
    };
    tickRef.current = tick;
    tick();
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      dead = true; tickRef.current = null;
      window.clearTimeout(timer.current); document.removeEventListener('visibilitychange', onVis);
    };
  }, [token, load]);

  // Something just changed on purpose: look now, and keep looking quickly.
  const kick = useCallback(() => { tickRef.current?.(); }, []);

  const create = useCallback(async (fileId, mode, segments) => {
    const j = await api.trimCreate(token, fileId, mode, segments);
    setJobs((l) => [j, ...l.filter((x) => x.id !== j.id)]);
    if (seen.current) seen.current.set(j.id, j.status);
    kick();
    return j;
  }, [token, kick]);

  const act = useCallback(async (fn, okMsg) => {
    try {
      const r = await fn();
      if (okMsg) cbs.current.toast?.('ok', ...okMsg);
      kick();
      return r;
    } catch (e) {
      cbs.current.toast?.('err', 'That did not work', e.message);
      return null;
    }
  }, [kick]);

  const discard = useCallback((j) => act(() => api.trimDiscard(token, j.id)), [act, token]);
  const retry = useCallback((j) => act(() => api.trimRetry(token, j.id)), [act, token]);
  const undo = useCallback(async (j) => {
    const r = await act(() => api.trimUndo(token, j.id), ['Original restored', j.file_current_name || j.file_name]);
    if (r) { cbs.current.onReplaced?.(j.file_id); cbs.current.onChanged?.(); }
    return r;
  }, [act, token]);

  const askSave = useCallback((j) => setDialog({ kind: 'save', job: j }), []);
  const askReplace = useCallback((j) => setDialog({ kind: 'replace', job: j }), []);
  const close = useCallback(() => setDialog(null), []);

  const node = dialog && (dialog.kind === 'save' ? (
    <SaveTrimModal token={token} job={dialog.job} drives={drives || []} onClose={close}
                   onSaved={() => { if (seen.current) seen.current.set(dialog.job.id, 'committing'); kick(); }}/>
  ) : (
    <ConfirmModal
      title="Replace the original"
      danger
      confirmLabel="Replace original"
      onClose={close}
      onConfirm={async () => {
        await api.trimReplace(token, dialog.job.id);
        if (seen.current) seen.current.set(dialog.job.id, 'committing');
        kick();
      }}
      body={<ReplaceBody job={dialog.job}/>}
    />
  ));

  // Opening a trim's video from the list opens its trim panel too, on it.
  const openFile = useCallback((j) => {
    setFocus({ fileId: j.file_id, jobId: j.id });
    cbs.current.onOpenFile?.(j);
  }, []);
  const showFile = useCallback((j) => cbs.current.onShowFile?.(j), []);

  const active = jobs.filter((j) => ACTIVE.includes(j.status)).length;
  return {
    token, jobs, active, load: kick, create, discard, retry, undo, askSave, askReplace,
    dialog: node, focus, setFocus, onShowFile: showFile, onOpenFile: openFile,
  };
}

function ReplaceBody({ job }) {
  const name = job.file_current_name || job.file_name;
  return (
    <>
      <p>
        <b>{name}</b> becomes the trimmed version: {fmtDur(job.out_duration)} and {fmtBytes(job.out_size)}
        {job.src_duration ? <>, down from {fmtDur(job.src_duration)}</> : null}.
      </p>
      <ul className="st-trim-list">
        <li>It keeps its place and its id, so links and shares to it keep working.</li>
        <li>Markers move with the cut; ones in a removed part land on the join.</li>
        <li>The original is kept for a week — Undo is in Transfers.</li>
        {job.out_ext && !name.toLowerCase().endsWith(`.${job.out_ext}`) && (
          <li>It will be renamed to end in <b>.{job.out_ext}</b>, which is what it now is.</li>
        )}
      </ul>
    </>
  );
}

function SaveTrimModal({ token, job, drives, onSaved, onClose }) {
  const [name, setName] = useState(() => suggestName(job));
  const [dest, setDest] = useState({ drive: job.drive, path: job.file_path || '/' });
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const driveRow = drives.find((d) => d.id === dest.drive);
  const submit = async (e) => {
    e?.preventDefault();
    const n = name.trim();
    if (!n) { setErr('Give it a name'); return; }
    if (n.includes('/')) { setErr('Names cannot contain a slash'); return; }
    setBusy(true); setErr('');
    try {
      await api.trimSave(token, job.id, { name: n, path: dest.path, drive: dest.drive });
      onSaved?.();
      onClose();
    } catch (ex) { setErr(ex.message || 'That did not work'); setBusy(false); }
  };
  if (picking) {
    return (
      <MoveModal token={token} drives={drives} fromDrive={dest.drive} fromPath={dest.path}
                 title="Save the trim in" confirmLabel="Choose this folder" busyLabel="…" crossDriveNote={null}
                 onMove={async (p, d) => { setDest({ drive: d, path: p }); }}
                 onClose={() => setPicking(false)}/>
    );
  }
  return (
    <Modal title="Save as a copy" onClose={onClose} footer={
      <>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn--sm" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Save copy'}</button>
      </>
    }>
      <form onSubmit={submit} className="field st-field">
        <label htmlFor="st-trim-name">Name</label>
        <input id="st-trim-name" className="input" value={name} autoFocus
               onChange={(e) => { setName(e.target.value); setErr(''); }}/>
        <span className="help">A new file, owned by you — the original stays as it is. {fmtBytes(job.out_size)} · {fmtDur(job.out_duration)}.</span>
      </form>
      <div className="st-trim-dest">
        <Icon name="folder" size={15}/>
        <span className="st-trim-destpath" title={`${driveRow?.label || dest.drive} ${dest.path}`}>
          <span className="meta">{driveRow?.label || dest.drive}</span> {dest.path}
        </span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setPicking(true)}>Change…</button>
      </div>
      {err && <div className="alert alert--err st-alert">{err}</div>}
    </Modal>
  );
}

/* ─── one job, as a card ─────────────────────────────────────────────── */

function partsText(job) {
  const parts = job.cuts || job.segments || [];
  const kept = keptOf(parts);
  return `${parts.length > 1 ? `${parts.length} parts · ` : ''}${fmtDur(kept)} kept`;
}

function eta(s) {
  if (!Number.isFinite(s) || s <= 0) return '';
  if (s < 60) return `${Math.ceil(s)}s left`;
  return `${Math.ceil(s / 60)}m left`;
}

/**
 * A trim's state and what can be done about it. `where` is 'player' (the
 * panel under a video: it can preview in place) or 'panel' (the transfers
 * list: it can open the video).
 */
export function TrimJob({ trim, job, where, previewing, onPreview }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const pct = Math.round((job.progress || 0) * 100);
  const live = job.status === 'queued' || job.status === 'working';
  const name = job.file_current_name || job.file_name;
  return (
    <div className={`st-tj st-tj--${job.status}${previewing ? ' is-preview' : ''}`}>
      <div className="st-tj-top">
        <Icon name="cut" size={13}/>
        {where === 'panel' ? (
          <button type="button" className="st-tj-name" title={`Open ${name}`} disabled={!job.file_exists}
                  onClick={() => trim.onOpenFile?.(job)}>{name}</button>
        ) : (
          <span className="st-tj-name st-tj-name--plain">{job.mode === 'fast' ? 'Fast' : 'Precise'} · {partsText(job)}</span>
        )}
        <span className={`st-tj-state meta${job.status === 'ready' ? ' is-ready' : ''}`}>{STATUS[job.status] || job.status}</span>
      </div>
      {where === 'panel' && <div className="st-tj-sub">{job.mode === 'fast' ? 'Fast' : 'Precise'} · {partsText(job)}</div>}

      {live && (
        <>
          <div className="bar st-tj-bar"><div className="fill" style={{ width: `${job.status === 'queued' ? 0 : pct}%` }}/></div>
          <div className="st-tj-meta tnum">
            <span>{job.status === 'queued'
              ? (job.queue_pos > 1 ? `${job.queue_pos - 1} ahead of it` : 'starting…')
              : `${pct}%`}</span>
            <span>{job.status === 'working' ? eta(job.eta) : ''}</span>
          </div>
        </>
      )}
      {job.status === 'committing' && (
        <div className="bar st-tj-bar"><div className="fill st-tj-indet"/></div>
      )}
      {job.status === 'ready' && (
        <div className="st-tj-meta tnum">
          <span>{fmtDur(job.out_duration)} · {fmtBytes(job.out_size)}</span>
          {job.expires_at && <span title="Unsaved results are cleared from the trim cache">kept until {fmtDate(job.expires_at)}</span>}
        </div>
      )}
      {job.error && (job.status === 'failed' || job.status === 'ready') && <div className="st-tj-err">{job.error}</div>}
      {job.status === 'committed' && (
        <div className="st-tj-meta">
          <span>
            {job.commit_kind === 'replace'
              ? `Replaced the original${job.undo_until ? ` · undo until ${fmtDate(job.undo_until)}` : ''}`
              : `Saved as ${job.commit_name}`}
          </span>
        </div>
      )}
      {job.status === 'evicted' && <div className="st-tj-meta">Cleared from the trim cache before it was saved.</div>}

      <div className="st-tj-acts">
        {live && <button type="button" className="st-trim-btn" disabled={busy} onClick={() => run(() => trim.discard(job))}>Cancel</button>}
        {job.status === 'ready' && (
          <>
            {where === 'player' ? (
              <button type="button" className={`st-trim-btn${previewing ? ' is-on' : ''}`} onClick={() => onPreview?.(previewing ? null : job)}>
                <Icon name={previewing ? 'close' : 'eye'} size={13}/> {previewing ? 'Back to original' : 'Preview'}
              </button>
            ) : (
              <button type="button" className="st-trim-btn" disabled={!job.file_exists} onClick={() => trim.onOpenFile?.(job)}>
                <Icon name="eye" size={13}/> Preview
              </button>
            )}
            <button type="button" className="st-trim-btn is-primary" onClick={() => trim.askSave(job)}>
              <Icon name="save" size={13}/> Save as copy
            </button>
            <button type="button" className="st-trim-btn" disabled={!job.can_replace}
                    title={job.can_replace ? 'Put the trimmed version in place of the original'
                      : (job.file_exists ? 'Only the owner or an admin can replace it (or it has changed since)' : 'The original is gone')}
                    onClick={() => trim.askReplace(job)}>
              Replace original
            </button>
            <a className="st-trim-btn" href={api.trimResultUrl(trim.token, job.id, true)} title="Download the result">
              <Icon name="download" size={13}/>
            </a>
            <button type="button" className="st-trim-btn" disabled={busy} onClick={() => run(() => trim.discard(job))}>Discard</button>
          </>
        )}
        {job.status === 'committed' && job.commit_kind === 'copy' && job.commit_file && trim.onShowFile && (
          <button type="button" className="st-trim-btn" onClick={() => trim.onShowFile(job)}>
            <Icon name="folder" size={13}/> Show
          </button>
        )}
        {job.status === 'committed' && job.undo_until && (
          <>
            <button type="button" className="st-trim-btn is-primary" disabled={busy || !job.can_undo}
                    title={job.can_undo ? 'Put the original back' : 'The file has changed since, or it is not yours'}
                    onClick={() => run(() => trim.undo(job))}>Undo</button>
            <button type="button" className="st-trim-btn" disabled={busy}
                    title="Stop keeping the original — frees its space in the trim cache"
                    onClick={() => run(() => trim.discard(job))}>Forget original</button>
          </>
        )}
        {['failed', 'cancelled', 'evicted'].includes(job.status) && (
          <>
            <button type="button" className="st-trim-btn is-primary" disabled={busy || !job.file_exists} onClick={() => run(() => trim.retry(job))}>Run again</button>
            <button type="button" className="st-trim-btn" disabled={busy} onClick={() => run(() => trim.discard(job))}>Dismiss</button>
          </>
        )}
      </div>
    </div>
  );
}

/** The trims section of the transfers panel. */
export function TrimJobs({ trim }) {
  const list = (trim?.jobs || []).filter((j) => j.status !== 'undone'
    && !(j.status === 'committed' && !j.undo_until && Date.now() / 1000 - (j.committed_at || 0) > 86400));
  if (!list.length) return null;
  return (
    <section className="st-trim-jobs" aria-label="Trims">
      <div className="st-srv-head">
        <span className="label">Trims</span>
        <span className="meta">{trim.active ? `${trim.active} on the way` : `${list.length}`}</span>
      </div>
      <div className="st-trim-joblist">
        {list.map((j) => <TrimJob key={j.id} trim={trim} job={j} where="panel"/>)}
      </div>
    </section>
  );
}

/** "Trimmed just now · Undo", over a video whose original was just replaced. */
export function TrimNotice({ trim, fileId }) {
  const [gone, setGone] = useState(false);
  const job = (trim?.jobs || []).find((j) => j.file_id === fileId && j.status === 'committed'
    && j.commit_kind === 'replace' && j.undo_until && Date.now() / 1000 - (j.committed_at || 0) < 1800);
  if (!job || gone) return null;
  return (
    <div className="st-vp-resume st-trim-notice">
      <span>Trimmed {fmtDate(job.committed_at)}</span>
      {job.can_undo && <button type="button" className="st-vp-link" onClick={() => trim.undo(job)}>Undo</button>}
      <button type="button" className="st-vp-link st-vp-link--dim" onClick={() => setGone(true)}>Hide</button>
    </div>
  );
}

/* ─── the panel in the player ────────────────────────────────────────── */

function TimeField({ label, value, onCommit, onStep, stepTitle, disabled }) {
  const [text, setText] = useState(null);     // non-null while being typed in
  const [bad, setBad] = useState(false);
  const commit = () => {
    if (text === null) return;
    const t = parseClock(text);
    if (t === null) { setBad(true); return; }
    setBad(false); setText(null);
    onCommit(t);
  };
  return (
    <div className={`st-trim-tf${bad ? ' is-bad' : ''}`}>
      <span className="st-trim-tfl">{label}</span>
      <button type="button" className="st-vp-btn st-trim-nudge" disabled={disabled} title={`${stepTitle} earlier (shift: 1s)`}
              aria-label={`${label} earlier`} onClick={(e) => onStep(-1, e.shiftKey)}>‹</button>
      <input className="st-trim-time tnum" value={text ?? fmtClock(value)} disabled={disabled}
             aria-label={`${label} time`} inputMode="decimal" spellCheck="false"
             onFocus={(e) => { setText(fmtClock(value)); window.setTimeout(() => e.target.select(), 0); }}
             onChange={(e) => { setText(e.target.value); setBad(false); }}
             onBlur={commit}
             onKeyDown={(e) => {
               if (e.key === 'Enter') { e.preventDefault(); commit(); e.currentTarget.blur(); }
               if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setText(null); setBad(false); e.currentTarget.blur(); }
             }}/>
      <button type="button" className="st-vp-btn st-trim-nudge" disabled={disabled} title={`${stepTitle} later (shift: 1s)`}
              aria-label={`${label} later`} onClick={(e) => onStep(1, e.shiftKey)}>›</button>
    </div>
  );
}

/**
 * The trim panel. `player` is what it needs of the MediaPlayer around it:
 * the element, its clock, and a way to swap the source for a result.
 * `keysRef` is how it takes over I and O while it is open.
 */
export function TrimTools({ trim, file, player, keysRef, onClose }) {
  const { mediaRef, duration, current, seekTo, say, previewJob, setPreviewJob } = player;
  const token = trim.token;
  const fileId = file.id;
  const [info, setInfo] = useState(null);
  const [infoErr, setInfoErr] = useState('');
  const draft0 = useMemo(() => readDrafts()[fileId] || null, [fileId]);
  const [segs, setSegs] = useState(draft0?.segs || null);
  const [sel, setSel] = useState(draft0?.sel || 0);
  const [mode, setMode] = useState(draft0?.mode || null);   // null: follow the smart default
  const [view, setView] = useState(null);                    // {from, to} when zoomed
  const [playCut, setPlayCut] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const stripRef = useRef(null);
  const drag = useRef(null);
  const seekRaf = useRef(0);

  // What the server knows about the video: keyframes take a moment on a
  // first visit, so ask again until they are there.
  useEffect(() => {
    let dead = false;
    let t = 0;
    const ask = async (n) => {
      try {
        const d = await api.trimInfo(token, fileId);
        if (dead) return;
        setInfo(d);
        if (d.state === 'working' && n < 120) t = window.setTimeout(() => ask(n + 1), n < 10 ? 700 : 2000);
      } catch (e) { if (!dead) setInfoErr(e.message); }
    };
    ask(0);
    return () => { dead = true; window.clearTimeout(t); };
  }, [token, fileId]);

  const probe = info?.probe;
  const fps = probe?.fps || 30;
  const frame = 1 / fps;
  const dur = Number.isFinite(duration) && duration > 0 ? duration : (probe?.duration || 0);
  const maxT = probe?.duration ? Math.min(dur, probe.duration) : dur;
  const kfs = info?.keyframes && info.keyframes.length ? info.keyframes : null;

  useEffect(() => {
    if (dur > 0 && (!segs || !segs.length || segs.some(([, b]) => b > dur + 0.5))) { setSegs([[0, dur]]); setSel(0); }
  }, [dur, segs]);
  useEffect(() => {
    if (segs) writeDraft(fileId, { segs, sel, mode });
  }, [fileId, segs, sel, mode]);

  const list = segs || [];
  const s = list[Math.min(sel, list.length - 1)] || [0, dur];
  const lead = list.map(([a]) => (kfs ? Math.max(0, a - kfBefore(kfs, a)) : null));
  const maxLead = lead.reduce((m, x) => Math.max(m, x || 0), 0);
  const repeats = list.some(([a], i) => i > 0 && kfs && kfBefore(kfs, a) < list[i - 1][1] - 0.01);
  const auto = kfs && (maxLead > DRIFT_OK || repeats) ? 'precise' : 'fast';
  const m = mode || auto;
  const kept = keptOf(list);
  const keptFast = kept + lead.reduce((n, x) => n + (x || 0), 0);
  const whole = list.length === 1 && s[0] <= 0.02 && s[1] >= dur - 0.05;
  const ext = (file.original_name || '').split('.').pop().toLowerCase();
  const outExt = info ? (m === 'fast' ? info.fast_ext : info.precise_ext) : null;
  const estSize = (() => {
    if (!file.size || !dur) return null;
    if (m === 'fast') return file.size * Math.min(1, keptFast / dur);
    const px = (probe?.width || 1920) * (probe?.height || 1080);
    return Math.min(px * Math.min(fps, 120) * 0.2 * kept / 8, file.size * (kept / dur) * 1.5);
  })();

  /* ── editing ────────────────────────────────────────────────────────── */
  const seekSoon = useCallback((t) => {
    window.cancelAnimationFrame(seekRaf.current);
    seekRaf.current = window.requestAnimationFrame(() => seekTo(t));
  }, [seekTo]);

  /** Move one edge of part i, held between its neighbours. */
  const setEdge = useCallback((i, edge, t, { seek = true } = {}) => {
    setSegs((L0) => {
      if (!L0 || !L0[i]) return L0;
      const L = L0.map((x) => [...x]);
      const [a, b] = L[i];
      if (edge === 'a') {
        const lo = i > 0 ? L[i - 1][1] : 0;
        L[i][0] = Math.max(lo, Math.min(b - MIN_PART, t));
      } else {
        const hi = i < L.length - 1 ? L[i + 1][0] : dur;
        L[i][1] = Math.min(hi, Math.max(a + MIN_PART, t));
      }
      if (seek) seekSoon(L[i][edge === 'a' ? 0 : 1]);
      return L;
    });
    setSel(i);
  }, [dur, seekSoon]);

  /** In at t: the part the playhead is in, or the next one along (drawn
   *  back to here), or the last one (moved out to here). */
  const setIn = useCallback((t) => {
    setSegs((L0) => {
      const L = (L0 || [[0, dur]]).map((x) => [...x]);
      let i = L.findIndex(([a, b]) => t >= a - 1e-6 && t < b);
      if (i < 0) i = L.findIndex(([a]) => a > t);
      if (i < 0) i = L.length - 1;
      const lo = i > 0 ? L[i - 1][1] : 0;
      const a = Math.max(lo, t);
      let b = L[i][1];
      if (a > b - MIN_PART) b = i < L.length - 1 ? L[i + 1][0] : dur;
      if (a > b - MIN_PART) { say('No room for an in point there'); return L0; }
      L[i] = [a, b];
      setSel(i);
      say(`In ${fmtClock(a)}`);
      return L;
    });
  }, [dur, say]);

  const setOut = useCallback((t) => {
    setSegs((L0) => {
      const L = (L0 || [[0, dur]]).map((x) => [...x]);
      let i = L.findIndex(([a, b]) => t > a && t <= b + 1e-6);
      if (i < 0) for (let j = L.length - 1; j >= 0; j -= 1) if (L[j][1] < t) { i = j; break; }
      if (i < 0) i = 0;
      const hi = i < L.length - 1 ? L[i + 1][0] : dur;
      const b = Math.min(hi, t);
      let a = L[i][0];
      if (b < a + MIN_PART) a = i > 0 ? L[i - 1][1] : 0;
      if (b < a + MIN_PART) { say('No room for an out point there'); return L0; }
      L[i] = [a, b];
      setSel(i);
      say(`Out ${fmtClock(b)}`);
      return L;
    });
  }, [dur, say]);

  const now = () => mediaRef.current?.currentTime ?? current;
  const inGap = !list.some(([a, b]) => current >= a && current < b);

  const split = useCallback(() => {
    const t = mediaRef.current?.currentTime ?? 0;
    setSegs((L0) => {
      const i = (L0 || []).findIndex(([a, b]) => t > a + MIN_PART && t < b - MIN_PART);
      if (i < 0) { say('Put the playhead inside a kept part to split it'); return L0; }
      const L = L0.map((x) => [...x]);
      L.splice(i, 1, [L0[i][0], t], [t, L0[i][1]]);
      setSel(i + 1);
      say('Split — now move an edge to open a gap');
      return L;
    });
  }, [mediaRef, say]);

  const addPart = useCallback(() => {
    const t = mediaRef.current?.currentTime ?? 0;
    setSegs((L0) => {
      const L = (L0 || []).map((x) => [...x]);
      if (L.some(([a, b]) => t >= a && t < b)) { say('Move the playhead to a part that is cut out'); return L0; }
      const next = L.find(([a]) => a > t);
      const b = Math.min(t + 5, next ? next[0] : dur);
      if (b - t < MIN_PART) { say('No room for a part there'); return L0; }
      L.push([t, b]);
      L.sort((x, y) => x[0] - y[0]);
      setSel(L.findIndex(([a]) => a === t));
      return L;
    });
  }, [mediaRef, dur, say]);

  const removePart = useCallback(() => {
    setSegs((L0) => {
      if (!L0 || L0.length < 2) return L0;
      const L = L0.filter((_, i) => i !== sel);
      setSel(Math.max(0, sel - 1));
      return L;
    });
  }, [sel]);

  const snapToKeyframes = useCallback(() => {
    if (!kfs) return;
    setSegs((L0) => {
      const L = (L0 || []).map(([a, b], i) => {
        const k = kfBefore(kfs, a);
        const lo = i > 0 ? L0[i - 1][1] : 0;
        return [k >= lo ? k : a, b];
      });
      say('In points moved onto keyframes');
      return L;
    });
  }, [kfs, say]);

  const reset = () => { setSegs([[0, dur]]); setSel(0); setMode(null); setView(null); };

  /* ── playing just the kept parts ────────────────────────────────────── */
  // What the result will look like, before making it: playback skips what is
  // cut out and goes round again at the end. In fast mode each part starts
  // where the fast cut really would.
  useEffect(() => {
    const el = mediaRef.current;
    if (!playCut || !el || !list.length || previewJob) return undefined;
    const starts = list.map(([a], i) => (m === 'fast' && kfs ? Math.min(a, kfBefore(kfs, a)) : a));
    if (!list.some(([, b], i) => el.currentTime >= starts[i] && el.currentTime < b)) el.currentTime = starts[0];
    el.play().catch(() => {});
    let raf = 0;
    const tick = () => {
      const t = el.currentTime;
      if (!el.paused) {
        const i = list.findIndex(([, b], j) => t >= starts[j] - 0.05 && t < b);
        if (i < 0) {
          const next = starts.findIndex((a) => a > t);
          el.currentTime = next >= 0 ? starts[next] : starts[0];
        }
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
    // The list is compared by value on purpose: dragging a handle mid-preview
    // should carry on with the new cut, not restart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playCut, JSON.stringify(list), m, kfs, previewJob, mediaRef]);

  /* ── keys ───────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!keysRef) return undefined;
    keysRef.current = (e) => {
      const k = e.key;
      const hit = (fn) => { e.preventDefault(); e.stopPropagation(); fn(); return true; };
      if (previewJob) return false;
      if (k === 'i') return hit(() => setIn(now()));
      if (k === 'o') return hit(() => setOut(now()));
      if (k === 'I') return hit(() => seekTo(s[0]));
      if (k === 'O') return hit(() => seekTo(s[1]));
      if (k === 'P') return hit(() => setPlayCut((v) => !v));
      if (k === 'S') return hit(split);
      if (k === 'Escape') return hit(onClose);
      return false;
    };
    return () => { keysRef.current = null; };
  });

  /* ── the strip ──────────────────────────────────────────────────────── */
  const win = view && dur ? view : { from: 0, to: dur || 1 };
  const span = Math.max(0.001, win.to - win.from);
  const pct = (t) => ((t - win.from) / span) * 100;
  const timeAt = (clientX) => {
    const r = stripRef.current.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return win.from + p * span;
  };

  // A zoomed strip follows the playhead rather than letting it walk off.
  useEffect(() => {
    if (!view || drag.current) return;
    if (current < view.from || current > view.to) {
      const w = view.to - view.from;
      const from = Math.max(0, Math.min(dur - w, current - w * 0.1));
      setView({ from, to: from + w });
    }
  }, [current, view, dur]);

  const zoom = (dir) => {
    if (!dur) return;
    const w0 = win.to - win.from;
    const w = dir > 0 ? Math.max(2, w0 / 2) : Math.min(dur, w0 * 2);
    if (w >= dur - 0.01) { setView(null); return; }
    const c = current;
    const from = Math.max(0, Math.min(dur - w, c - w / 2));
    setView({ from, to: from + w });
  };

  const onStripDown = (e) => {
    if (e.button !== 0 || !stripRef.current) return;
    const t = timeAt(e.clientX);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
    drag.current = { kind: 'seek' };
    const i = list.findIndex(([a, b]) => t >= a && t <= b);
    if (i >= 0) setSel(i);
    seekTo(t);
  };
  const onStripMove = (e) => {
    if (!drag.current || !stripRef.current) return;
    let t = timeAt(e.clientX);
    if (drag.current.kind === 'seek') { seekSoon(t); return; }
    const { i, edge } = drag.current;
    if (edge === 'a' && m === 'fast' && kfs) {
      // Magnetic keyframes: within a few pixels, an in point lands on one.
      const k = nearestKf(kfs, t);
      const r = stripRef.current.getBoundingClientRect();
      if (k !== null && Math.abs(((k - t) / span) * r.width) < 8) t = k;
    }
    setEdge(i, edge, t);
  };
  const onStripUp = (e) => {
    drag.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
  };
  const onHandleDown = (i, edge) => (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    try { stripRef.current.setPointerCapture(e.pointerId); } catch { /* not capturable */ }
    drag.current = { kind: 'edge', i, edge };
    setSel(i);
    setPlayCut(false);
    mediaRef.current?.pause();
    seekTo(list[i][edge === 'a' ? 0 : 1]);
  };
  const onHandleKey = (i, edge) => (e) => {
    const cur = list[i][edge === 'a' ? 0 : 1];
    let d = 0;
    if (e.key === 'ArrowLeft') d = -1;
    else if (e.key === 'ArrowRight') d = 1;
    else return;
    e.preventDefault(); e.stopPropagation();
    if (edge === 'a' && m === 'fast' && kfs && !e.shiftKey) {
      const idx = kfs.indexOf(kfBefore(kfs, cur));
      const k = d < 0 ? kfs[cur - kfs[idx] > 0.001 ? idx : idx - 1] : kfs[idx + 1];
      if (k !== undefined) setEdge(i, edge, k);
      return;
    }
    setEdge(i, edge, cur + d * (e.shiftKey ? 1 : frame));
  };

  const step = (edge) => (dir, big) => {
    const cur = s[edge === 'a' ? 0 : 1];
    if (edge === 'a' && m === 'fast' && kfs && !big) {
      const idx = kfs.indexOf(kfBefore(kfs, cur));
      const k = dir < 0 ? kfs[cur - kfs[idx] > 0.001 ? idx : idx - 1] : kfs[idx + 1];
      if (k !== undefined) setEdge(sel, edge, k);
      return;
    }
    setEdge(sel, edge, cur + dir * (big ? 1 : frame));
  };

  const gaps = [];
  let gapAt = 0;
  for (const [a, b] of list) { if (a > gapAt) gaps.push([gapAt, a]); gapAt = b; }
  if (dur > gapAt) gaps.push([gapAt, dur]);
  const ticks = kfs && m === 'fast' ? kfs.filter((k) => k >= win.from && k <= win.to) : [];
  const tickStep = Math.max(1, Math.ceil(ticks.length / 400));

  /* ── making it ──────────────────────────────────────────────────────── */
  const start = async () => {
    setBusy(true); setErr('');
    try {
      const parts = mergeParts(list).map(([a, b]) => [Math.max(0, a), Math.min(maxT, b)]);
      const j = await trim.create(fileId, m, parts);
      setPlayCut(false);
      say(j.status === 'queued' ? 'Trim queued' : 'Trimming');
    } catch (e) { setErr(e.message || 'Could not start the trim'); }
    finally { setBusy(false); }
  };

  const mine = (trim.jobs || []).filter((j) => j.file_id === fileId && j.mine
    && ['queued', 'working', 'ready', 'committing', 'failed'].includes(j.status)).slice(0, 2);
  const running = mine.some((j) => j.status === 'queued' || j.status === 'working');
  const wantNotify = running && 'Notification' in window && Notification.permission === 'default';

  if (previewJob) {
    const j = (trim.jobs || []).find((x) => x.id === previewJob.id) || previewJob;
    return (
      <div className="st-trim is-previewing">
        <div className="st-trim-head">
          <span className="st-trim-title"><Icon name="eye" size={14}/> Previewing the trimmed result</span>
        </div>
        <TrimJob trim={trim} job={j} where="player" previewing onPreview={setPreviewJob}/>
      </div>
    );
  }

  return (
    <div className="st-trim" aria-label="Trim">
      <div className="st-trim-head">
        <span className="st-trim-title"><Icon name="cut" size={14}/> Trim</span>
        <div className="st-trim-modes" role="radiogroup" aria-label="How to cut">
          {[['fast', 'Fast'], ['precise', 'Precise']].map(([id, label]) => (
            <button key={id} type="button" role="radio" aria-checked={m === id}
                    className={`st-trim-mode${m === id ? ' is-on' : ''}`}
                    onClick={() => setMode(id)}>
              {label}{!mode && auto === id && <span className="st-trim-auto">auto</span>}
            </button>
          ))}
        </div>
        <button type="button" className="st-trim-btn is-primary st-trim-start" onClick={start}
                disabled={busy || !segs || whole || !info || !info.enabled}
                title={whole ? 'Move the in or out point first' : `Make the ${m} trim — you can watch it before saving`}>
          <Icon name="cut" size={13}/> {busy ? 'Starting…' : 'Trim'}
        </button>
        <button type="button" className="st-vp-btn" onClick={onClose} title="Close trim (Esc)" aria-label="Close trim">
          <Icon name="close" size={14}/>
        </button>
      </div>

      {mine.length > 0 && (
        <div className="st-trim-joblist">
          {mine.map((j) => (
            <TrimJob key={j.id} trim={trim} job={j} where="player" previewing={false} onPreview={setPreviewJob}/>
          ))}
        </div>
      )}

      <div className="st-trim-stripwrap">
        <div ref={stripRef} className={`st-trim-strip${view ? ' is-zoomed' : ''}`}
             onPointerDown={onStripDown} onPointerMove={onStripMove} onPointerUp={onStripUp} onPointerCancel={onStripUp}>
          {gaps.map(([a, b]) => (
            <div key={`g${a}`} className="st-trim-gap" style={{ left: `${pct(a)}%`, width: `${pct(b) - pct(a)}%` }}/>
          ))}
          {ticks.filter((_, i) => i % tickStep === 0).map((k) => (
            <span key={`k${k}`} className="st-trim-kf" style={{ left: `${pct(k)}%` }}/>
          ))}
          {list.map(([a, b], i) => (
            <React.Fragment key={`s${i}`}>
              {m === 'fast' && lead[i] > 0.01 && (
                <div className="st-trim-lead" title={`The fast cut starts ${lead[i].toFixed(2)}s earlier, on a keyframe`}
                     style={{ left: `${pct(a - lead[i])}%`, width: `${pct(a) - pct(a - lead[i])}%` }}/>
              )}
              <div className={`st-trim-seg${i === sel ? ' is-sel' : ''}`}
                   style={{ left: `${pct(a)}%`, width: `${pct(b) - pct(a)}%` }}>
                {list.length > 1 && <span className="st-trim-segn">{i + 1}</span>}
              </div>
            </React.Fragment>
          ))}
          <div className="st-trim-play" style={{ left: `${pct(current)}%` }}/>
          {list.map(([a, b], i) => (
            <React.Fragment key={`h${i}`}>
              <span className={`st-trim-h st-trim-h--a${i === sel ? ' is-sel' : ''}`} style={{ left: `${pct(a)}%` }}
                    role="slider" tabIndex={0} aria-label={`Part ${i + 1} in`}
                    aria-valuemin={0} aria-valuemax={Math.round(dur)} aria-valuenow={Math.round(a)} aria-valuetext={fmtClock(a)}
                    onPointerDown={onHandleDown(i, 'a')} onKeyDown={onHandleKey(i, 'a')} onFocus={() => setSel(i)}/>
              <span className={`st-trim-h st-trim-h--b${i === sel ? ' is-sel' : ''}`} style={{ left: `${pct(b)}%` }}
                    role="slider" tabIndex={0} aria-label={`Part ${i + 1} out`}
                    aria-valuemin={0} aria-valuemax={Math.round(dur)} aria-valuenow={Math.round(b)} aria-valuetext={fmtClock(b)}
                    onPointerDown={onHandleDown(i, 'b')} onKeyDown={onHandleKey(i, 'b')} onFocus={() => setSel(i)}/>
            </React.Fragment>
          ))}
        </div>
        <div className="st-trim-scale meta tnum">
          <span>{fmtClock(win.from)}</span>
          <span className="st-trim-zoom">
            <button type="button" className="st-vp-btn" onClick={() => zoom(-1)} disabled={!view} title="Zoom out" aria-label="Zoom out">−</button>
            {view
              ? <button type="button" className="st-vp-link st-vp-link--dim" onClick={() => setView(null)}>show all</button>
              : <span>zoom</span>}
            <button type="button" className="st-vp-btn" onClick={() => zoom(1)} disabled={!dur || (win.to - win.from) <= 2.01} title="Zoom in around the playhead" aria-label="Zoom in">+</button>
          </span>
          <span>{fmtClock(win.to)}</span>
        </div>
      </div>

      <div className="st-trim-row">
        <TimeField label="In" value={s[0]} stepTitle={m === 'fast' && kfs ? 'Keyframe' : 'Frame'}
                   onCommit={(t) => setEdge(sel, 'a', t)} onStep={step('a')} disabled={!segs}/>
        <TimeField label="Out" value={s[1]} stepTitle="Frame"
                   onCommit={(t) => setEdge(sel, 'b', t)} onStep={step('b')} disabled={!segs}/>
        <span className="st-trim-kept tnum" title="What the result will be">
          Keep <b>{fmtDur(m === 'fast' ? keptFast : kept)}</b> of {fmtDur(dur)}
          {estSize ? <span className="meta"> · ≈{fmtBytes(estSize)}</span> : null}
        </span>
      </div>

      <div className="st-trim-row st-trim-tools">
        <button type="button" className="st-trim-btn" onClick={() => setIn(now())} title="Set the in point at the playhead (I)">
          <span className="st-trim-key">I</span> Set in
        </button>
        <button type="button" className="st-trim-btn" onClick={() => setOut(now())} title="Set the out point at the playhead (O)">
          <span className="st-trim-key">O</span> Set out
        </button>
        <button type="button" className={`st-trim-btn${playCut ? ' is-on' : ''}`} onClick={() => setPlayCut((v) => !v)}
                title="Play only what is kept, round and round (shift+P)" aria-pressed={playCut}>
          <Icon name={playCut ? 'pause' : 'refresh'} size={13}/> {playCut ? 'Stop' : 'Play the cut'}
        </button>
        <span className="st-trim-sep" aria-hidden="true"/>
        <button type="button" className="st-trim-btn" onClick={split} disabled={inGap}
                title="Split the part under the playhead in two, to cut something out of the middle (shift+S)">Split</button>
        <button type="button" className="st-trim-btn" onClick={addPart} disabled={!inGap || list.length >= (info?.max_segments || 20)}
                title="Keep a part of what is cut out, starting at the playhead">+ Part</button>
        {list.length > 1 && (
          <button type="button" className="st-trim-btn" onClick={removePart} title={`Drop part ${sel + 1}`}>
            − Part {sel + 1}
          </button>
        )}
        <button type="button" className="st-trim-btn" onClick={reset} title="Back to the whole video">Reset</button>
      </div>

      <div className="st-trim-why">
        {!info && !infoErr && <span className="meta">Reading the video…</span>}
        {infoErr && <span className="st-tj-err">{infoErr}</span>}
        {info && m === 'fast' && (
          <p>
            <b>Fast</b> copies the picture untouched — seconds, not minutes, and nothing is lost. A cut can only
            start on a keyframe, {info.state === 'working' ? <span className="meta">(finding them…) </span> : null}
            {!kfs && info.state !== 'working' ? 'so it may start a little early.' : maxLead > 0.01
              ? <>so {list.length > 1 ? 'a part' : 'it'} starts up to <b>{maxLead.toFixed(2)}s early</b> (the amber bit). <button type="button" className="st-vp-link" onClick={snapToKeyframes}>Move in points onto keyframes</button></>
              : 'and every in point is on one: the cut is exact.'}
            {repeats && <> <span className="st-trim-warn">A part would repeat the end of the one before — use precise.</span></>}
          </p>
        )}
        {info && m === 'precise' && (
          <p>
            <b>Precise</b> re-encodes what you keep, so every cut lands on the exact frame. It takes a while
            (it runs at low priority on the server — you can close this and keep browsing) and re-compresses
            the picture at visually lossless quality.
          </p>
        )}
        {outExt && outExt !== ext && <p className="meta">The result will be a .{outExt} file.</p>}
      </div>

      {(err || wantNotify) && (
        <div className="st-trim-go">
          {err && <span className="st-tj-err">{err}</span>}
          {wantNotify && (
            <button type="button" className="st-vp-link" onClick={() => Notification.requestPermission().catch(() => {})}>
              Notify me when it is done
            </button>
          )}
        </div>
      )}

    </div>
  );
}
